import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  commonConstants,
  schedulePaymentsConstants,
} from "../../../../_redux/_constants";
import { UiPopup } from "../../../_uiComponents/common/UiModal";
import { Container } from "react-bootstrap";
import { TabPanel } from "@mui/lab";
import { UiBackBreadCrumb } from "../../../_uiComponents/UiBackBreadCrumb";
import { UiWhiteCard } from "../../../_uiComponents/common/UiWhiteCard";
import { UiScheduleSummary } from "../../../_uiComponents/SchedulePayments/UiScheduleSummary";
import { UiNavTab } from "../../../_uiComponents/common/UiNavTab";
import { UiSchedulePaymentIterationCard } from "../../../_uiComponents/SchedulePayments/UiSchedulePaymentIterationCard";
import UISchedulePaymentsEmpty from "../../../_uiComponents/SchedulePayments/UISchedulePaymentsEmpty";
import { UiGrayBgLoader } from "../../../_uiComponents/common/UiGrayBgLoader";
import {
  changeIterationStatusRequest,
  scheduleIterationListRequest,
  setActiveView,
  setScheduleDetailActiveTab,
  setSelectedSchedulePaymentsIterationDetails,
  stopSchedulePaymentRequest,
} from "../../../../_redux/_actions/schedulePayments.actions";
import { ConfirmationPopupWithCustomButton } from "../../../_uiComponents/Popups/ConfirmationPopup";
import { ConfirmationIcon } from "../../../../styles/img/svg/ConfirmationIcon";

const SchedulePaymentSummary = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showRetryModal, setShowRetryModal] = useState(false);
  const [openStopSchedulesAll, setOpenStopSchedulesAll] = useState(false);

  const {
    schedulePaymentsDetails,
    isIterationListLoading,
    scheduleIterationsData,
    isStopSchedulePaymentLoading,
    scheduleDetailActiveTab,
  } = useSelector((state) => state.schedulePayments);

  const activeTab =
    scheduleDetailActiveTab ??
    schedulePaymentsConstants.SCHEDULE_DETAIL_TAB_UPCOMING_SCHEDULES;

  const resolveScheduledType = (details) => {
    const t = details?.scheduledPaymentType || details?.scheduledType;
    if (t === schedulePaymentsConstants.SCHEDULED_PAYMENT_TYPE_BILL)
      return schedulePaymentsConstants.SCHEDULED_PAYMENT_TYPE_BILL;
    return schedulePaymentsConstants.SCHEDULED_PAYMENT_TYPE_TRANSFER;
  };

  const iterationViewRequestRef = useRef(null);

  /**
   * Initial load: Schedule Payment Iteration List (per get-schedule-payment-iteration-list-api.md).
   * Requires `recurringTransactionTemplateId` on `schedulePaymentsDetails` (from list row).
   */
  useEffect(() => {
    const templateId = schedulePaymentsDetails?.recurringTransactionTemplateId;
    if (!templateId) return;

    dispatch(
      scheduleIterationListRequest({
        recurringTransactionTemplateId: templateId,
        scheduledType: schedulePaymentsDetails?.scheduledPaymentType,
        isPastIteration:
          activeTab ===
          schedulePaymentsConstants.SCHEDULE_DETAIL_TAB_PAST_SCHEDULES
            ? commonConstants.YES_OPTION_TYPE
            : commonConstants.NO_OPTION_TYPE,
        pageStart: 0,
        pageEnd: 4,
      }),
    );
  }, [dispatch, schedulePaymentsDetails, activeTab]);

  /**
   * Optional: call change-iteration-status API once when opening summary.
   * Set `iterationStatusChangeOnView: { status: 'STOP'|'ACTIVE', scheduledType?: 'TRANSFER'|'BILL' }`
   * on the object passed to `setSchedulePaymentsDetails` from the list row.
   */
  useEffect(() => {
    const d = schedulePaymentsDetails;
    if (!d) {
      iterationViewRequestRef.current = null;
      return;
    }
    const iterationId = d.iterationId ?? d.recurringTransactionIterationId;
    const cfg = d.iterationStatusChangeOnView;
    if (!iterationId || !cfg?.status) return;
    const key = `${iterationId}:${cfg.status}`;
    if (iterationViewRequestRef.current === key) return;
    iterationViewRequestRef.current = key;
    dispatch(
      changeIterationStatusRequest({
        iterationId,
        status: cfg.status,
        scheduledType: cfg.scheduledType ?? resolveScheduledType(d),
      }),
    );
  }, [dispatch, schedulePaymentsDetails]);

  const backFromForm = () => {
    dispatch(setActiveView());
    navigate("/app/schedule-payments");
  };

  const tabList = [
    {
      id: 1,
      label: "Upcoming Schedules",
      value: schedulePaymentsConstants.SCHEDULE_DETAIL_TAB_UPCOMING_SCHEDULES,
    },
    {
      id: 2,
      label: "Past Schedules",
      value: schedulePaymentsConstants.SCHEDULE_DETAIL_TAB_PAST_SCHEDULES,
    },
  ];

  const handleSchedulePaymentIterationDetail = (iterationData) => {
    dispatch(setSelectedSchedulePaymentsIterationDetails(iterationData));

    dispatch(
      setActiveView(
        schedulePaymentsConstants.ACTIVE_VIEW_SCHEDULE_PAYMENT_ITERATION_DETAIL,
      ),
    );
  };

  const handleRetrySchedulePayment = () => {
    setShowRetryModal(false);
  };

  const handleStopSchedulesAll = () => {
    const d = schedulePaymentsDetails;
    if (!d?.recurringTransactionTemplateId) return;
    dispatch(
      stopSchedulePaymentRequest({
        recurringTransactionTemplateId: d.recurringTransactionTemplateId,
        scheduledType: resolveScheduledType(d),
      }),
    );
  };

  const handleCloseModal = () => {
    setOpenStopSchedulesAll(false);
  };

  return (
    <>
      <div className="df-user-profile">
        <Container maxWidth="md" className="df-pos-rel">
          <div className="df-user-profile__body df-bill-payment-form">
            <div>
              <UiBackBreadCrumb
                backOnClick={backFromForm}
                titleText="Schedule Details"
                disableCloseButton
              />

              <UiWhiteCard className="df-schedule-detail-form__wrapper df-app-setting">
                <div className="df-common-setting-cards">
                  <UiScheduleSummary
                    scheduleSummary={schedulePaymentsDetails}
                    onStopAllClick={() => setOpenStopSchedulesAll(true)}
                    isStopLoading={isStopSchedulePaymentLoading}
                  />
                </div>

                <UiNavTab
                  tabNav={tabList}
                  value={activeTab}
                  onSelect={(selectedTab) => {
                    dispatch(setScheduleDetailActiveTab(selectedTab));
                  }}
                  className="df-schedule-tab-wrapper"
                >
                  <TabPanel
                    value={
                      schedulePaymentsConstants.SCHEDULE_DETAIL_TAB_UPCOMING_SCHEDULES
                    }
                    className="df-pad-lr-0"
                  >
                    <span className="df-schedule-detail-form__title">
                      Most recent first five schedules
                    </span>

                    {!isIterationListLoading ? (
                      <>
                        {scheduleIterationsData?.length > 0 ? (
                          scheduleIterationsData.map((iteration, idx) => (
                            <UiSchedulePaymentIterationCard
                              key={
                                iteration?.recurringTransactionIterationId ??
                                iteration?.iterationCount ??
                                idx
                              }
                              iterationData={iteration}
                              onClick={() => {
                                handleSchedulePaymentIterationDetail(iteration);
                              }}
                              scheduleSummary={schedulePaymentsDetails}
                            />
                          ))
                        ) : (
                          <UISchedulePaymentsEmpty />
                        )}
                      </>
                    ) : (
                      <UiGrayBgLoader className="df-ch-130" />
                    )}
                  </TabPanel>

                  <TabPanel
                    value={
                      schedulePaymentsConstants.SCHEDULE_DETAIL_TAB_PAST_SCHEDULES
                    }
                    className="df-pad-lr-0"
                  >
                    {!isIterationListLoading ? (
                      <>
                        {scheduleIterationsData?.length > 0 ? (
                          scheduleIterationsData?.map((schedule, idx) => (
                            <UiSchedulePaymentIterationCard
                              key={
                                schedule?.recurringTransactionIterationId ??
                                schedule?.iterationCount ??
                                idx
                              }
                              iterationData={schedule}
                              onClick={() =>
                                handleSchedulePaymentIterationDetail(schedule)
                              }
                              scheduleSummary={schedulePaymentsDetails}
                            />
                          ))
                        ) : (
                          <UISchedulePaymentsEmpty />
                        )}
                      </>
                    ) : (
                      <UiGrayBgLoader className="df-ch-130" />
                    )}
                  </TabPanel>
                </UiNavTab>
              </UiWhiteCard>
            </div>
          </div>
        </Container>
      </div>

      <UiPopup openModal={openStopSchedulesAll} handleClose={handleCloseModal}>
        <ConfirmationPopupWithCustomButton
          icon={<ConfirmationIcon />}
          CancelButtonName="Cancel"
          SuccessButtonName="Yes"
          message="Stop All Schedules"
          buttonOnClick={handleStopSchedulesAll}
          className="df-confirmation-message-popup df-btn-grpdmodal"
          buttonOnClickClose={handleCloseModal}
        >
          <p>Are you sure you want stop all these schedules completely?</p>
        </ConfirmationPopupWithCustomButton>
      </UiPopup>

      <UiPopup
        openModal={showRetryModal}
        handleClose={() => setShowRetryModal(false)}
      >
        <button type="button" onClick={() => handleRetrySchedulePayment()}>
          Retry
        </button>
      </UiPopup>
    </>
  );
};

export default SchedulePaymentSummary;
